import React, { useEffect, useState } from "react";
import GoogleCalendarButton from "./header/GoogleCalendarButton";
import {
  HeartIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";

const PROVIDER_LABELS = {
  ultrahuman: "Ultrahuman",
  oura: "Oura Ring",
  fitbit: "Fitbit",
  garmin: "Garmin",
  whoop: "WHOOP",
  apple_health: "Apple Health",
  google_fit: "Google Fit",
  samsung: "Samsung Health",
  withings: "Withings",
  polar: "Polar",
  coros: "COROS",
  suunto: "Suunto",
  peloton: "Peloton",
  eight_sleep: "Eight Sleep",
  strava: "Strava",
};

const formatProvider = (slug) =>
  PROVIDER_LABELS[slug] || slug.charAt(0).toUpperCase() + slug.slice(1);

const Connect = ({ userId }) => {
  const [healthLink, setHealthLink] = useState("");
  const [isSynced, setIsSynced] = useState(false);
  const [isHealthSynced, setIsHealthSynced] = useState(false);
  const [healthProviders, setHealthProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;

    // Single API call for everything
    axios
      .get(`/connect_status/${userId}`)
      .then(({ data }) => {
        setIsSynced(data.calendar_connected);
        setIsHealthSynced(data.health_connected);
        setHealthProviders(data.provider_details || []);
        setHealthLink(data.health_link || "");
      })
      .catch((error) => {
        console.error("Error fetching connect status:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [userId]);

  return (
    <div className="">
      <h3 className="text-xl text-slate-400 font-bold text-left mb-2">
        Connect your data
      </h3>
      <p className="text-slate-500 text-sm mb-4">
        Your coach gets smarter with more context. Connect your calendar and
        health devices to unlock personalized insights.
      </p>
      <div className="flex flex-col justify-start gap-3">
        {/* Calendar Connection */}
        <GoogleCalendarButton
          userId={userId}
          isSynced={isSynced}
          isLoading={isLoading}
        />

        {/* Health Device Connection */}
        {isLoading ? (
          <div className="inline-flex items-center justify-between w-full px-4 py-3.5 rounded-lg bg-slate-800 text-sm font-medium text-white">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-500/10 mr-3">
                <ArrowPathIcon className="h-4 w-4 text-red-400 animate-spin" />
              </div>
              <span>Checking health connection...</span>
            </div>
          </div>
        ) : isHealthSynced ? (
          <div className="w-full rounded-lg bg-slate-800 border border-emerald-500/20 overflow-hidden">
            <div className="px-4 py-3.5 flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500/10 mr-3">
                  <HeartIcon className="h-4 w-4 text-emerald-400" />
                </div>
                <div className="text-left">
                  <span className="block font-semibold text-sm text-white">
                    Health Devices Connected
                  </span>
                  <span className="block text-xs text-slate-400 mt-0.5">
                    Syncing sleep, activity, heart rate &amp; more
                  </span>
                </div>
              </div>
              <CheckCircleIcon className="h-5 w-5 text-emerald-500 flex-shrink-0" />
            </div>

            {/* Connected providers list */}
            {healthProviders.length > 0 && (
              <div className="px-4 pb-3 pt-1 border-t border-slate-700/50">
                <span className="text-xs text-slate-500 uppercase tracking-wide">
                  Connected devices
                </span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {healthProviders.map((provider) => (
                    <span
                      key={provider.slug}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                    >
                      {provider.logo && (
                        <img
                          src={provider.logo}
                          alt={provider.name}
                          className="w-4 h-4 rounded-full"
                        />
                      )}
                      {provider.name || formatProvider(provider.slug)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Add another device link */}
            <a
              href={healthLink}
              className="block px-4 py-2.5 text-xs text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors border-t border-slate-700/50 text-center"
            >
              + Connect another device
            </a>
          </div>
        ) : (
          <a
            href={healthLink}
            className="group relative inline-flex items-center justify-between w-full lg:w-auto px-4 py-3.5 rounded-lg bg-slate-800 text-sm font-medium text-white shadow-sm hover:bg-slate-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-500/10 mr-3">
                <HeartIcon className="h-4 w-4 text-red-400" />
              </div>
              <div className="text-left">
                <span className="block font-semibold">
                  Connect Health Devices
                </span>
                <span className="block text-xs text-slate-400 mt-0.5">
                  Sleep, activity, heart rate &amp; more
                </span>
              </div>
            </div>
            <ChevronRightIcon className="h-5 w-5 ml-2 text-slate-400 group-hover:text-white transition-colors" />
          </a>
        )}

        {/* Benefits - only show when not connected */}
        {!isHealthSynced && !isLoading && (
          <div className="mt-2 space-y-2">
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-slate-500">
                500+ devices supported: Apple Watch, Oura, Fitbit, Garmin,
                Whoop &amp; more
              </span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-slate-500">
                Your coach uses real data to give better advice on sleep,
                recovery, and energy
              </span>
            </div>
          </div>
        )}
      </div>
      <p className="text-slate-600 text-xs mb-6 mt-4">
        By connecting your data, you agree to our terms. You can disconnect at
        any time. See our{" "}
        <a
          href="/privacy-policy"
          className="text-blue-600 hover:text-blue-800"
        >
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
};

export default Connect;
